import { motion } from 'framer-motion';
import { X, ArrowDownLeft, ArrowUpRight, Ban, Gift, Receipt, Copy, ChevronRight, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

export const SITE_LABELS = {
  specialist_dental_london: { label: 'SDL', color: 'bg-[#C8A86B]/15 text-[#96793E]' },
  london_dental_academy: { label: 'LDA', color: 'bg-blue-50 text-blue-700' },
  shihab_romeed: { label: 'SR', color: 'bg-stone-100 text-stone-700' },
};

export const TX_TYPE_STYLES = {
  payment:    { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ArrowDownLeft, amountColor: 'text-emerald-700', label: 'Payment' },
  refund:     { bg: 'bg-red-50 text-red-700 border-red-200', icon: ArrowUpRight, amountColor: 'text-red-600', label: 'Refund' },
  adjustment: { bg: 'bg-blue-50 text-blue-700 border-blue-200', icon: Receipt, amountColor: 'text-blue-600', label: 'Adjustment' },
  void:       { bg: 'bg-stone-100 text-stone-500 border-stone-200', icon: Ban, amountColor: 'text-stone-400', label: 'Void' },
  credit:     { bg: 'bg-violet-50 text-violet-700 border-violet-200', icon: Gift, amountColor: 'text-violet-600', label: 'Credit' },
};

export const STATUS_STYLES = {
  completed: 'bg-emerald-50 text-emerald-700',
  initiated: 'bg-amber-50 text-amber-700',
  expired:   'bg-stone-100 text-stone-500',
  voided:    'bg-red-50 text-red-600',
  refunded:  'bg-violet-50 text-violet-700',
  pending:   'bg-amber-50 text-amber-700',
  paid:      'bg-emerald-50 text-emerald-700',
};

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs text-stone-400 shrink-0">{label}</span>
      <div className="text-right ml-4">{value}</div>
    </div>
  );
}

export default function TransactionDetailPanel({ tx, apiFetch, onClose, onUpdate, navigate }) {
  const ts = TX_TYPE_STYLES[tx.type] || TX_TYPE_STYLES.payment;
  const TIcon = ts.icon;
  const isVoided = !!tx.voidedById || tx.status === 'voided';
  const method = tx.metadata?.method || (tx.stripePaymentIntentId ? 'stripe' : tx.type);
  const siteInfo = SITE_LABELS[tx.bookingSiteKey || tx.siteKey] || { label: '?', color: 'bg-stone-100 text-stone-600' };
  const isStripePayment = !!tx.stripePaymentIntentId && tx.type === 'payment';
  const isStripeRefund = !!tx.stripeRefundId && tx.type === 'refund';
  // Allow voiding any transaction except void transactions themselves, and Stripe-linked payments/refunds
  const canVoid = !isVoided && tx.type !== 'void' && !isStripePayment && !isStripeRefund;
  const canRefund = !isVoided && isStripePayment && tx.status === 'completed';

  const handleVoid = async () => {
    const reason = window.prompt('Reason for voiding this transaction:');
    if (!reason?.trim()) return;
    try {
      const res = await apiFetch('/payments/void', { method: 'POST', body: { transactionId: tx.id, reason } });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      toast.success('Transaction voided');
      onUpdate?.();
    } catch (err) { toast.error(err.message); }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(tx.id);
    toast.success('Transaction ID copied');
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl border-l border-stone-200 z-[70] flex flex-col">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-stone-100 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border capitalize', ts.bg)}>
                  <TIcon className="w-3.5 h-3.5" /> {ts.label}
                </span>
                {tx.status !== 'completed' && (
                  <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full capitalize', STATUS_STYLES[tx.status] || 'bg-stone-100 text-stone-500')}>{tx.status}</span>
                )}
              </div>
              <h3 className={cn('text-2xl font-bold mt-2', isVoided ? 'line-through text-stone-400' : ts.amountColor)}>
                {tx.type === 'refund' ? '-' : ''}£{tx.amount?.toFixed(2)}
              </h3>
              <p className="text-xs text-stone-400 mt-0.5">{format(new Date(tx.createdAt), "d MMMM yyyy 'at' HH:mm")}</p>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors shrink-0 ml-3">
              <X className="w-4 h-4 text-stone-500" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">

            {/* Transaction Details */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Details</h4>
              <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100">
                <DetailRow label="Booking" value={
                  <span className="font-mono text-xs font-semibold text-stone-800">{tx.bookingRef}</span>
                } />
                <DetailRow label="Site" value={
                  <span className={cn('inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider', siteInfo.color)}>{siteInfo.label}</span>
                } />
                <DetailRow label="Method" value={<span className="capitalize text-sm text-stone-700">{method}</span>} />
                <DetailRow label="Date" value={<span className="text-sm text-stone-700">{format(new Date(tx.createdAt), 'd MMM yyyy, HH:mm')}</span>} />
                <DetailRow label="Relative" value={<span className="text-sm text-stone-500">{formatDistanceToNow(new Date(tx.createdAt), { addSuffix: true })}</span>} />
                {tx.stripePaymentIntentId && <DetailRow label="Stripe PI" value={
                  <button onClick={() => { navigator.clipboard.writeText(tx.stripePaymentIntentId); toast.success('Copied'); }}
                    className="font-mono text-[11px] text-stone-600 hover:text-stone-900 flex items-center gap-1 transition-colors">
                    {tx.stripePaymentIntentId.slice(0, 20)}... <Copy className="w-3 h-3" />
                  </button>
                } />}
                {tx.stripeRefundId && <DetailRow label="Stripe Refund" value={
                  <span className="font-mono text-[11px] text-stone-600">{tx.stripeRefundId}</span>
                } />}
                {tx.recordedBy && <DetailRow label="Recorded By" value={<span className="text-sm font-medium text-stone-700">{tx.recordedBy}</span>} />}
                {tx.reason && <DetailRow label="Reason" value={<span className="text-sm text-stone-700">{tx.reason}</span>} />}
                {tx.metadata?.notes && <DetailRow label="Notes" value={<span className="text-sm text-stone-600">{tx.metadata.notes}</span>} />}
                {tx.voidsTransactionId && <DetailRow label="Voids" value={<span className="text-xs text-red-600 font-mono">Transaction voided</span>} />}
                {tx.voidedById && <DetailRow label="Voided By" value={<span className="text-xs text-red-600">This transaction has been voided</span>} />}
              </div>
            </div>

            {/* Booking Context */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Booking Context</h4>
              <div className="bg-white border border-stone-200 rounded-xl divide-y divide-stone-100">
                <DetailRow label="Service" value={<span className="text-sm font-medium text-stone-800">{tx.serviceName}</span>} />
                <DetailRow label="Charged" value={<span className="text-sm font-semibold text-stone-800">£{tx.bookingAmountCharged?.toFixed(2)}</span>} />
                <DetailRow label="Paid" value={<span className="text-sm font-semibold text-emerald-700">£{tx.bookingAmountPaid?.toFixed(2)}</span>} />
                <DetailRow label="Balance" value={
                  <span className={cn('text-sm font-bold', tx.bookingBalance > 0 ? 'text-amber-600' : 'text-emerald-700')}>£{tx.bookingBalance?.toFixed(2)}</span>
                } />
              </div>
            </div>

            {/* Transaction ID */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">System</h4>
              <button onClick={handleCopyId}
                className="w-full text-left bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs font-mono text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors flex items-center justify-between">
                <span className="truncate">ID: {tx.id}</span>
                <Copy className="w-3 h-3 shrink-0 ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="shrink-0 px-6 py-4 border-t border-stone-100 bg-stone-50/50">
          <div className="flex gap-2">
            {canVoid && (
              <Button onClick={handleVoid} variant="outline" className="flex-1 h-10 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
                <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Void Transaction
              </Button>
            )}
            {canRefund && (
              <Button onClick={async () => {
                const amount = window.prompt(`Refund amount (max £${tx.amount.toFixed(2)}):`);
                if (!amount) return;
                const reason = window.prompt('Reason for refund:');
                if (!reason?.trim()) return;
                try {
                  const res = await apiFetch('/payments/refund', { method: 'POST', body: { bookingId: tx.bookingId, amount: parseFloat(amount), method: 'stripe', reason } });
                  if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
                  toast.success(`£${parseFloat(amount).toFixed(2)} refunded via Stripe`);
                  onUpdate?.();
                } catch (err) { toast.error(err.message); }
              }} variant="outline" className="flex-1 h-10 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50">
                <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Refund via Stripe
              </Button>
            )}
            <Button variant="outline" onClick={() => { navigate(`/admin/patients/${tx.patientId}`); onClose(); }}
              className="h-10 text-xs font-semibold">
              View Patient <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
